import type { ShopperSearch } from '@/scapi';

/** SCAPI attribute id for the price refinement. */
export const PRICE_FACET_ATTRIBUTE_ID = 'price';

/** SFCC inclusive-max workaround offset applied when writing price refine values. */
export const PRICE_MAX_OFFSET = 0.009;

export type PriceBounds = {
    absoluteMin: number;
    absoluteMax: number;
};

export type PriceRange = {
    min: number;
    max: number;
};

type ProductSearchRefinement = ShopperSearch.schemas['ProductSearchRefinement'];

const RANGE_RE = /\(([^)]*)\.\.([^)]*)\)/;

/** Parse a `(min..max)` string into numeric bounds (no flooring). */
export function parsePriceRangeString(value: string | undefined): { min: number; max: number } | null {
    if (!value) {
        return null;
    }
    const match = value.match(RANGE_RE);
    if (!match) {
        return null;
    }
    const min = parseFloat(match[1]);
    const max = parseFloat(match[2]);
    if (isNaN(min) || isNaN(max)) {
        return null;
    }
    return { min, max };
}

/**
 * Absolute slider bounds from the SCAPI price refinement value.
 * Applies the SFCC +0.009 max offset before ceil, matching EatalyRefinePrice.
 */
export function getPriceSliderBounds(values: NonNullable<ProductSearchRefinement['values']> | undefined): PriceBounds {
    const priceValue = values?.[0]?.value;
    const parsed = parsePriceRangeString(priceValue);
    if (!parsed) {
        return { absoluteMin: 0, absoluteMax: 1000 };
    }

    let finalMin = Math.floor(parsed.min);
    let finalMax = Math.ceil(parsed.max + PRICE_MAX_OFFSET);

    if (finalMin === finalMax) {
        finalMin = Math.max(0, finalMin - 10);
        finalMax = finalMax + 10;
    }

    return { absoluteMin: finalMin, absoluteMax: finalMax };
}

export function hasPriceFacetRefinement(refinements: ProductSearchRefinement[] | undefined): boolean {
    const price = refinements?.find((r) => r.attributeId === PRICE_FACET_ATTRIBUTE_ID);
    const values = price?.values ?? [];
    if (values.length !== 1) {
        return false;
    }
    if ((values[0]?.hitCount ?? 0) <= 0) {
        return false;
    }
    return parsePriceRangeString(values[0]?.value) != null;
}

export function parsePriceRangeFromRefines(refines: string[]): PriceRange | null {
    const prefix = `${PRICE_FACET_ATTRIBUTE_ID}=`;
    const priceRefine = refines.find((r) => r.startsWith(prefix));
    if (!priceRefine) {
        return null;
    }
    const parsed = parsePriceRangeString(priceRefine.slice(prefix.length));
    if (!parsed) {
        return null;
    }
    return { min: Math.floor(parsed.min), max: Math.floor(parsed.max) };
}

export function hasActivePriceFacet(refines: string[]): boolean {
    return parsePriceRangeFromRefines(refines) != null;
}

/** Build the SCAPI refine value string with the inclusive-max offset. */
export function formatPriceRefineValue(min: number, max: number): string {
    return `(${min}..${max + PRICE_MAX_OFFSET})`;
}

/**
 * Strip existing price refine pairs, then optionally append a single range value.
 * Pass `null` to clear the price facet only.
 */
export function replacePriceRefine(currentRefines: string[], rangeString: string | null): string[] {
    const prefix = `${PRICE_FACET_ATTRIBUTE_ID}=`;
    const withoutPrice = currentRefines.filter((r) => !r.startsWith(prefix));
    if (!rangeString) {
        return withoutPrice;
    }
    return [...withoutPrice, `${PRICE_FACET_ATTRIBUTE_ID}=${rangeString}`];
}
